// NOTE: This is copied from API project. Should be kept in sync with it.

import { ISessionRoomStats } from "@/phaser/classes/session/Session";
import { IPlayer, ISessionPlayer } from "./game";

export enum ThemeType {
  Construction = "Construction",
  FinancialServices = "Financial Services",
  Government = "Government",
  Healthcare = "Healthcare",
  Utilities = "Utilities",
  MixAndMatch = "Mix and match",
}

export enum GameMissionObjective {
  Unknown = "",
  FindBlueprint = "FindBlueprint",
  FindReport = "FindReport",
  RebootSystem = "RebootSystem",
}

export enum SessionStatus {
  Pending = "pending",
  Started = "started",
  Finished = "finished",
}

export enum GameStatus {
  New = "new",
  Draft = "draft",
  Published = "published",
}

export interface IApiResponse<T> {
  success?: boolean;
  statusCode?: number;
  message?: string;
  data?: T;
  count?: number;
}

export interface IApiError {
  message: string;
  error?: string;
  statusCode: number;
}

export interface IDisplayImageActionPayload {
  image: string;
  hint: string;
  hintVisible?: boolean;
}

export interface IDisplayImageWithStateActionPayload extends IDisplayImageActionPayload {
  state: string;
}

export interface IDisplayImageByStateActionPayload {
  images: IDisplayImageWithStateActionPayload[];
}

export interface IDisplayUnlockScreenActionPayload {
  image: string;
  text: string;
}

export interface IDisplayMessageActionPayload {
  message: string;
}

export interface IAddToInventoryActionPayload {
  inspection: boolean;
  message: string;
}

export interface IDisplayPuzzleActionPayload {
  title: string;
  puzzle: string;
}

export interface INewProperties {
  isVisible?: boolean;
  isDisabled?: boolean;
  currentState?: string;
  currentAction?: number;
}

export interface IMediaActionPayload {
  ref: string;
  mediaUrl?: string;
  type: string;
  message?: string;
}

export interface IChoices {
  ref: string;
  answers: string[];
  correctAnswer: number;
  randomiseAnswers: boolean;
}

export interface IQuestion {
  _id: string;
  ref: string;
  type: string; //"multipleChoice" | "textAnswer";
  uiTitle?: string;
  title?: string; // Implemented as the actual question
  choices?: IChoices;
  answer?: string;
  mediaUrl?: string;
  mediaPayload?: IMediaActionPayload;
}

export interface IQuestionnaireActionPayload {
  ref: string;
  questions?: IQuestion[];
}

export interface IActionBullets {
  text: string;
  children?: string[];
}

export type IActionPayload =
  | IDisplayImageActionPayload
  | IDisplayImageByStateActionPayload
  | IDisplayUnlockScreenActionPayload
  | IDisplayMessageActionPayload
  | IAddToInventoryActionPayload
  | IDisplayPuzzleActionPayload
  | IQuestionnaireActionPayload
  | IMediaActionPayload;

export interface IAction {
  _id: string;
  score: number;
  ref: string;
  trigger: string;
  type: string;
  isVisibleOnAdminPanel: boolean;
  description?: string;
  bullets?: IActionBullets[];
  triggerItemRef: string;
  repeatable?: boolean;
  payload?: IActionPayload;
  lastState?: unknown;
}

export interface IState {
  name: string;
  textureUrl: string;
  xOffset: number;
  yOffset: number;
}

export interface IAsset {
  _id: string;
  name: string;
  category: string;
  defaultColorPalette: string[];
  states: Map<string, IState>;
}

export interface IRoomObject {
  _id: string;
  name: string;
  ref: string;
  order: number;
  asset: IAsset;
  isVisible: boolean;
  isDisabled: boolean;
  currentState: string;
  xPos: number;
  yPos: number;
  actions: IAction[];
  currentAction: number;
  colorPalette: Array<string>;
}

export interface IRoom {
  _id: string;
  name: string;
  description: string;
  theme: ThemeType;
  originalOrder: number;
  assetPrefix: string;
  bullets: string[];
  ref: string;
  isVisible: boolean;
  isActive: boolean;
  length: number;
  order: number;
  objects: IRoomObject[];
  items: string[];
  hintsCounter: number;
  puzzlesCounter: number;
  numberOfPuzzles: number;
  numberOfQuestionnaires: number;
  countdown: number;
  puzzleTime: number;
}

export interface IGame {
  _id: string;
  name: string;
  client?: string;
  clientLogoId?: string;
  industry?: string;
  theme?: string;
  description?: string;
  ref: string;
  status: string;
  time: number;
  rooms: IRoom[];
  colorPalette: string[];
  creator: string;
  creatorEmail: string;
  introductionHeader: string;
  introduction: string;
  introductionMediaId?: string;
  displayIntroduction: boolean;
  gameMissionObjective?: GameMissionObjective | "";
  gameMission: string;
  gameMissionReplacementStrings: string[];
  outroSuccessfulMessage: string;
  outroUnsuccessfulMessagePart1: string;
  outroUnsuccessfulMessagePart2: string;
  schemaVersion: number;
}

export enum RoomType {
  OpenEnded,
  End,
}

export interface IGameTemplateRoomDefinitionDto {
  theme: ThemeType;
  number: number;
}

export interface IGameTemplateBaseDto {
  theme: ThemeType;
  industry: string;
  rooms: string;
  puzzles: number;
  name: string;
  client: string;
  clientLogoId: string;
  gameDuration: string;
}

export interface IGameTemplateDto extends IGameTemplateBaseDto {
  numberOfQuestions: string;
  trainingQuestions: string;
}

export interface IGameTemplateMixDto extends IGameTemplateBaseDto {
  roomsDefinition: IGameTemplateRoomDefinitionDto[];
}

export interface ICoordinate {
  xPos: number;
  yPos: number;
}

export interface ICursor {
  currentMousePos: ICoordinate;
  color: string;
  playerName: string;
}

export interface IAnsweredQuestions {
  actionId: Map<
    string,
    {
      results: Array<IQuestion>;
      totalAttempts: number;
      totalScore: number;
    }
  >;
}

export interface ILockedItem {
  playerId: string;
  roomObjectId: string;
}

export interface IFeedItemType {
  templateString: string;
}

export interface IFeedItem {
  subject: IPlayer;
  object: IPlayer | IRoomObject | string;
  feedItemType: IFeedItemType;
  sourceClientId?: string;
  createdAt: Date;
}

export interface IChatItem {
  agent: IPlayer;
  message: string;
}

export interface IActiveQuestionnaire {
  roomId: string;
  roomObjectId: string;
  actionId: string;
  currentActiveQuestionIds: Map<string, string>;
  finishedPlayers: string[];
}

export interface IGameStats {
  [playerId: string]: {
    answeredQuestions: number;
    firstAttemptPercentage: number;
  };
}

export interface IGameSession {
  _id: string;
  name: string;
  players: ISessionPlayer[];
  numberOfPlayers: number;
  pickedUpItems: string[];
  teamCursors: ICursor[];
  teamScore: number;
  teamName: string;
  currentNavigatorPlayerId: string | null;
  activeRoomId: string;
  activePuzzleId: string | null;
  gameId: string;
  game: IGame;
  inventory: IRoomObject[];
  lockedObjects: Map<string, ILockedItem>;
  timer: number;
  activityFeed: IFeedItem[];
  chatFeed: IChatItem[];
  gameStarted?: Date;
  gameCompleted?: Date;
  roomsStats?: ISessionRoomStats;
  activeQuestionnaire: IActiveQuestionnaire;
  status: string;
  description?: string;
  callLink?: string;
  answersSubmitted: number;
  answersCorrectAtFirstAttempt: number;
  creator: string;
  completedQuestionnaires: string[];
  gameStats: IGameStats;
  plannedDate?: string | null;
  plannedTime?: string | null;
  timeZone?: string;
}
